import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { clearCartDataFromCache } from "@magento/peregrine/lib/Apollo/clearCartDataFromCache";
import BrowserPersistence from "@magento/peregrine/lib/util/simplePersistence";
import { signOut } from "@magento/peregrine/lib/store/actions/user";
import { reset } from "../bookTable";
import actions from "./actions";
import { clearCustomerDataFromCache } from "@magento/peregrine/lib/Apollo/clearCustomerDataFromCache";
import { clearCheckoutDataFromStorage } from "../checkout";
import { resetBook_a_Table } from "../../../../../redux/bookTable/BookTable.actions";
import * as Sentry from "@sentry/react";
const storage = new BrowserPersistence();
export const submitOutOfStockClean = payload => async function thunk(dispatch) {
  dispatch(actions.outOfStockClean.receive(payload));
};
export const createCart = payload => async function thunk(dispatch, getState) {
  const {
    fetchCartId
  } = payload;
  const {
    cart
  } = getState();

  // if a cart already exists in the store, exit
  if (cart.cartId) {
    return;
  }

  // Request a new cart.
  dispatch(actions.getCart.request());

  // if a cart exists in storage, act like we just received it
  const cartId = await retrieveCartId();
  if (cartId) {
    dispatch(actions.getCart.receive(cartId));
    return;
  }
  try {
    // errors can come from graphql and are not thrown
    const {
      data,
      errors
    } = await fetchCartId({
      fetchPolicy: "no-cache"
    });
    let receivePayload;
    if (errors) {
      receivePayload = new Error(errors);
    } else {
      receivePayload = data.cartId;
      // Persist before downstream flows read the cart id back.
      await saveCartId(data.cartId);
    }
    dispatch(actions.getCart.receive(receivePayload));
  } catch (error) {
    // If we are unable to create a cart, the cart can't function, so
    // we forcibly throw so the upstream actions won't retry.
    dispatch(actions.getCart.receive(error));
    throw new Error("Unable to create cart");
  }
};
export const updateCartData = details => async function thunk(dispatch) {
  dispatch(actions.getDetails.receive({
    details
  }));
};
export const setIsLoadingCart = () => async function thunk(dispatch, getState) {
  const {
    cart
  } = getState();
  dispatch(actions.getDetails.request(cart));
};
export const addItemToCart = function () {
  let payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    addItemMutation,
    cartItems
  } = payload;
  return async function thunk(dispatch, getState) {
    dispatch(actions.addItem.request(payload));
    try {
      await addItemMutation();
      dispatch(actions.addItem.receive());
    } catch (error) {
      dispatch(actions.addItem.receive(error));
    }
  };
};

/**
 * Applies changes in options/quantity to a cart item.
 *
 * @param payload.cartItemId {Number} the id of the cart item we are updating
 * @param payload.item {Object} the new configuration item if changes are selected.
 * @param payload.quantity {Number} the quantity of the item being updated
 * @param payload.productType {String} 'ConfigurableProduct' or other.
 */
export const updateItemInCart = function () {
  let payload = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    cartItemId,
    fetchCartDetails,
    fetchCartId,
    item,
    productType,
    quantity,
    removeItem,
    updateItem
  } = payload;
  return async function thunk(dispatch, getState) {
    dispatch(actions.updateItem.request(payload));
    const {
      cart,
      user
    } = getState();
    const {
      cartId
    } = cart;
    const {
      isSignedIn
    } = user;
    try {
      if (productType === "ConfigurableProduct") {
        // You _must_ remove before adding or risk deleting the item
        // entirely if only quantity has been modified.
        await dispatch(removeItemFromCart({
          item: {
            id: cartItemId
          },
          fetchCartDetails,
          fetchCartId,
          removeItem
        }));
        await dispatch(addItemToCart(_objectSpread({}, payload)));
      } else {
        // If the product is a simple product we can just use the
        // updateCartItems graphql mutation.
        await updateItem({
          variables: {
            cartId,
            itemId: cartItemId,
            quantity
          }
        });
        // The configurable product conditional dispatches actions that
        // each call getCartDetails. For simple items we must request
        // details after the mutation completes. This may change when
        // we migrate to the `cart` query for details, away from REST.
        await dispatch(getCartDetails({
          fetchCartId,
          fetchCartDetails
        }));
      }
      dispatch(actions.updateItem.receive());
    } catch (error) {
      dispatch(actions.updateItem.receive(error));
      const shouldRetry = !error.networkError && isInvalidCart(error);
      if (shouldRetry) {
        // Delete the cached ID from local storage and Redux.
        // In contrast to the save, make sure storage deletion is
        // complete before dispatching the error--you don't want an
        // upstream action to try and reuse the known-bad ID.
        await dispatch(removeCart());

        // then create a new one
        try {
          await dispatch(createCart({
            fetchCartId
          }));
        } catch (error) {
          // If creating a cart fails, all is not lost. Return so that the
          // user can continue to at least browse the site.
          return;
        }

        // and fetch details
        await dispatch(getCartDetails({
          fetchCartId,
          fetchCartDetails
        }));
        if (isSignedIn) {
          // The user is signed in and we just received their cart.
          // Retry this operation.
          return thunk(...arguments);
        } else {
          // The user is a guest and just received a brand new (empty) cart.
          // Add the updated item to that cart.
          await dispatch(addItemToCart(_objectSpread({}, payload)));
        }
      }
    }
  };
};
export const removeItemFromCart = payload => {
  const {
    item,
    fetchCartDetails,
    fetchCartId,
    removeItem
  } = payload;
  return async function thunk(dispatch, getState) {
    dispatch(actions.removeItem.request(payload));
    const {
      cart
    } = getState();
    const {
      cartId
    } = cart;
    try {
      await removeItem({
        variables: {
          cartId,
          itemId: item.id
        }
      });
      dispatch(actions.removeItem.receive());
    } catch (error) {
      dispatch(actions.removeItem.receive(error));
      const shouldResetCart = !error.networkError && isInvalidCart(error);
      if (shouldResetCart) {
        // Delete the cached ID from local storage.
        // The reducer handles clearing out the bad ID from Redux.
        // In contrast to the save, make sure storage deletion is
        // complete before dispatching the error--you don't want an
        // upstream action to try and reuse the known-bad ID.
        await dispatch(removeCart());
        // then create a new one
        try {
          await dispatch(createCart({
            fetchCartId
          }));
        } catch (error) {
          // If creating a cart fails, all is not lost. Return so that the
          // user can continue to at least browse the site.
          return;
        }
      }
    }
    await dispatch(getCartDetails({
      fetchCartId,
      fetchCartDetails
    }));
  };
};
export const setIsMiniCartOpen = payload => async function thunk(dispatch, getState) {
  dispatch(actions.setIsMiniCartOpen.recieve(payload));
};
export const getCartDetails = payload => {
  const {
    apolloClient,
    fetchCartId,
    fetchCartDetails
  } = payload;
  return async function thunk(dispatch, getState) {
    const {
      cart,
      user
    } = getState();
    const {
      cartId
    } = cart;
    const {
      isSignedIn
    } = user;

    // if there isn't a cart, create one then retry this operation
    if (!cartId) {
      try {
        await dispatch(createCart({
          fetchCartId
        }));
      } catch (error) {
        // If creating a cart fails, all is not lost. Return so that the
        // user can continue to at least browse the site.
        Sentry.addBreadcrumb({
          category: "cart",
          message: "createCart() failed",
          data: {
            error: error
          }
        });
        Sentry.captureMessage("createCart() failed", "error");
        return;
      }
      return thunk(...arguments);
    }

    // Once we have the cart id indicate that we are starting to make
    // async requests for the details.
    dispatch(actions.getDetails.request(cartId));
    try {
      const {
        data
      } = await fetchCartDetails({
        variables: {
          cartId
        },
        skip: !cartId,
        fetchPolicy: "no-cache"
      });
      const {
        cart: details
      } = data;
      dispatch(actions.getDetails.receive({
        details
      }));
    } catch (error) {
      dispatch(actions.getDetails.receive(error));
      const shouldResetCart = !error.networkError && isInvalidCart(error);
      if (shouldResetCart) {
        Sentry.captureMessage("Should reset cart", "warning");

        // Delete the cached ID from local storage.
        await dispatch(removeCart());
        await dispatch(resetBook_a_Table());

        // Clear the cart data from apollo client if we get here and
        // have an apolloClient.
        if (apolloClient) {
          await clearCartDataFromCache(apolloClient);
          await clearCustomerDataFromCache(apolloClient);
          await clearCheckoutDataFromStorage();
        }

        // Create a new one
        try {
          await dispatch(() => {
            createCart({
              fetchCartId
            });
          });
        } catch (error) {
          // If creating a cart fails, all is not lost. Return so that the
          // user can continue to at least browse the site.
          return;
        }

        // Retry this operation
        return thunk(...arguments);
      }
    }
  };
};
export const removeCart = () => async function thunk(dispatch) {
  // Clear the cartId from local storage.
  await clearCartId();

  // Clear the cart info from the redux store.
  dispatch(actions.reset());
};

/* helpers */
export async function retrieveCartId() {
  return storage.getItem("cartId");
}
export async function saveCartId(id) {
  return storage.setItem("cartId", id);
}
export async function clearCartId() {
  storage.removeItem("cartId");
  const cartId = storage.getItem("cartId");
  if (cartId) {
    Sentry.addBreadcrumb({
      category: "cart",
      message: "clearCartId finished executing",
      level: "debug",
      data: {
        result: cartId
      }
    });
    Sentry.captureMessage("Failed to clear cartId", "error");
  }
}

// Returns true if the cart is invalid.
function isInvalidCart(error) {
  return !!(error.graphQLErrors && error.graphQLErrors.find(err => err.message.includes("Could not find a cart") || err.message.includes("The cart isn't active") || err.message.includes("The current user cannot perform operations on cart") || err.message.includes("Can't assign cart to store in different website.")));
}